import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createOrder, formatGBP } from "../lib/api";
import AcceptedPaymentsRow from "../components/AcceptedPaymentsRow";
import { useSEO } from "../hooks/useSEO";

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  notes: "",
};

const Field = ({ label, name, value, onChange, type = "text", required = true, placeholder, testid }) => (
  <label className="block">
    <span className="sb-mono-label">{label}{required && " *"}</span>
    <input
      data-testid={testid}
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-3 text-sm bg-white"
    />
  </label>
);

const Checkout = () => {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  useSEO({
    title: "Secure guest checkout",
    description: "Sterling Bio guest checkout — SSL-secured, free UK shipping, pay by bank transfer. For laboratory research only.",
    path: "/checkout",
  });

  const validate = () => {
    if (items.length === 0) return "Your cart is empty.";
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!/^.+@.+\..+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.address_line_1.trim()) return "Please enter your address.";
    if (!form.city.trim()) return "Please enter your city.";
    if (!form.postcode.trim()) return "Please enter your postcode.";
    if (!agree) return "Please confirm the research-use compliance statement.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      };
      const order = await createOrder(payload);
      clearCart();
      toast.success(`Order ${order.order_number} created`);
      // access_token authenticates the guest to view their own order.
      // Without it the order page returns 404 (prevents UUID-bearer scraping).
      const tokenParam = order.access_token ? `?token=${encodeURIComponent(order.access_token)}` : "";
      navigate(`/order/${order.id}${tokenParam}`);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Could not place order. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not place order.");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#64748B]" data-testid="checkout-empty">
        Your cart is empty.{" "}
        <Link to="/products" className="text-[#2C8C9C] sb-link-underline">View catalogue</Link>.
      </div>
    );

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="sb-mono-label flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Secure checkout · SSL 256-bit</div>
      <h1 className="mt-2 text-4xl font-semibold text-[#1B3A5C] tracking-tight" style={{ fontFamily: "Outfit" }}>
        Guest checkout
      </h1>
      <p className="mt-2 inline-flex items-center gap-2 text-sm text-[#047857]">
        <Truck className="w-4 h-4" strokeWidth={1.75} /> Free UK shipping on every order
      </p>

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-[#E2E8F0] p-6">
            <div className="sb-mono-label">01 · Contact</div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Field label="Full name" name="full_name" value={form.full_name} onChange={onChange} testid="checkout-fullname" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="checkout-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="checkout-phone" />
            </div>
          </section>

          <section className="border border-[#E2E8F0] p-6">
            <div className="sb-mono-label">02 · Shipping address</div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Address line 1" name="address_line_1" value={form.address_line_1} onChange={onChange} testid="checkout-address1" />
              </div>
              <div className="sm:col-span-2">
                <Field label="Address line 2" required={false} name="address_line_2" value={form.address_line_2} onChange={onChange} testid="checkout-address2" />
              </div>
              <Field label="City" name="city" value={form.city} onChange={onChange} testid="checkout-city" />
              <Field label="Postcode" name="postcode" value={form.postcode} onChange={onChange} testid="checkout-postcode" />
              <Field label="Country" name="country" value={form.country} onChange={onChange} testid="checkout-country" />
            </div>
          </section>

          <section className="border border-[#E2E8F0] p-6">
            <div className="sb-mono-label">03 · Notes (optional)</div>
            <textarea
              data-testid="checkout-notes"
              name="notes"
              rows={3}
              value={form.notes}
              onChange={onChange}
              placeholder="Anything our team should know about your order…"
              className="mt-3 w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none p-3 text-sm bg-white"
            />
          </section>

          <section className="border border-[#E2E8F0] p-6 bg-[#F8FAFC]">
            <div className="sb-mono-label">04 · Payment</div>
            <p className="mt-3 text-sm text-[#475569] leading-relaxed">
              Payment is by UK bank transfer via <strong>Pay-by-Bank</strong> (Open Banking).
              After you place your order you&apos;ll be shown a &quot;Pay by bank&quot; button and
              redirected to your bank&apos;s app to approve the transfer — no card details are
              entered, and no card data is stored by Sterling Bio.
            </p>
          </section>
        </div>

        <aside className="border border-[#E2E8F0] p-6 h-fit bg-white sticky top-28">
          <div className="sb-mono-label">Order summary</div>
          <ul className="mt-4 space-y-3 text-sm">
            {items.map((i) => (
              <li key={i.product_id} className="flex items-start justify-between gap-2 border-b border-[#E2E8F0] pb-3 last:border-b-0">
                <div>
                  <div className="font-medium">{i.name}</div>
                  <div className="font-mono text-xs text-[#64748B]">{i.dosage} × {i.quantity}</div>
                </div>
                <div className="font-mono text-sm">{formatGBP(i.unit_price_gbp * i.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-[#475569]">Subtotal</span>
              <span className="font-mono">{formatGBP(subtotal)}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between text-sm">
              <span className="text-[#475569]">UK shipping</span>
              <span data-testid="checkout-shipping" className="font-mono text-[#047857] font-medium uppercase">Free</span>
            </div>
            <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-baseline justify-between">
              <span className="text-sm font-medium text-[#1B3A5C]">Total</span>
              <span data-testid="checkout-total" className="font-mono text-xl font-semibold text-[#1B3A5C]">
                {formatGBP(subtotal)}
              </span>
            </div>
          </div>

          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <input
              data-testid="checkout-agree"
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#2C8C9C]"
            />
            <span className="text-xs text-[#475569] leading-relaxed">
              I confirm I am a qualified researcher and understand these products are{" "}
              <strong>strictly not for human or veterinary consumption</strong>. I accept the{" "}
              <Link to="/terms" className="text-[#2C8C9C] underline">Terms</Link>,{" "}
              <Link to="/refund-policy" className="text-[#2C8C9C] underline">Refund Policy</Link>,{" "}
              <Link to="/privacy-policy" className="text-[#2C8C9C] underline">Privacy Policy</Link> and{" "}
              <Link to="/compliance" className="text-[#2C8C9C] underline">Compliance Disclaimer</Link>.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="checkout-submit"
            className="mt-6 w-full bg-[#1B3A5C] text-white px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-[#254D7A] transition-colors disabled:opacity-50"
          >
            {loading ? "Placing order…" : "Place order"}
          </button>

          <div className="mt-4 flex items-center gap-2 text-xs text-[#64748B] font-mono uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5 text-[#2C8C9C]" /> SSL · Encrypted submission
          </div>

          {/* Payment methods visible at checkout — Wallid Pay-by-Bank only */}
          <div className="mt-5 pt-5 border-t border-[#E2E8F0]">
            <AcceptedPaymentsRow />
            <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-[#64748B]">
              Payments handled securely by Wallid via UK Open Banking. No card details entered.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default Checkout;
